import { queryOptions } from "@tanstack/react-query";
import type { ApiListRequest } from "../../../../@types/services/Api";
import type { DeliveryDateChangeRequestFilterType } from "../../../../@types/services/DeliveryDateChangeRequest";
import { useListPlantsQueryOptions } from "../../../../__generated__/orval/endpoints/plants/plants";
import type { EntitiesPlantListResponse } from "../../../../__generated__/orval/models/entitiesPlantListResponse.zod";
import {
  fetchDeliveryDateChangeRequest,
  fetchDeliveryDateChangeRequests,
} from "../../../../services/DeliveryDateChangeRequest";
import { fetchPurchaseGroups } from "../../../../services/purchaseGroup";
import { fetchUser, fetchUsers } from "../../../../services/users";
import { fetchVendors } from "../../../../services/vendors";

export const deliveryDateChangeRequestsListQueryOptions = (
  params: ApiListRequest<DeliveryDateChangeRequestFilterType>,
) =>
  queryOptions({
    queryFn: () => fetchDeliveryDateChangeRequests(params),
    queryKey: ["deliveryDateChangeRequests", "list", params],
  });

export const deliveryDateChangeRequestsDetailQueryOptions = (
  id: string | undefined,
) =>
  queryOptions({
    enabled: !!id,
    // biome-ignore lint/style/noNonNullAssertion: ID is checked in enabled
    queryFn: () => fetchDeliveryDateChangeRequest(id!),
    queryKey: ["deliveryDateChangeRequests", "detail", id],
  });

export const relatedRequestQueryOptions = (
  filters: DeliveryDateChangeRequestFilterType,
) =>
  queryOptions({
    queryFn: () => fetchDeliveryDateChangeRequests({ filters }),
    queryKey: ["deliveryDateChangeRequests", "list", filters],
  });

// Filters

export const userDetailsQueryOptions = (id: number | undefined) =>
  queryOptions({
    enabled: !!id,
    // biome-ignore lint/style/noNonNullAssertion: ID is checked in enabled
    queryFn: () => fetchUser(id!),
    queryKey: ["users", "detail", id],
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const userListQueryOptions = (ids: number[] | undefined) =>
  queryOptions({
    enabled: !!ids && ids.length > 0,
    queryFn: () => fetchUsers({ filters: { id: ids } }),
    queryKey: ["users", "list", ids],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const vendorListQueryOptions = (ids: number[] | undefined) =>
  queryOptions({
    enabled: !!ids && ids.length > 0,
    queryFn: () => fetchVendors({ filters: { id: ids } }),
    queryKey: ["vendors", "list", ids],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const plantListQueryOptions = (
  ids?: EntitiesPlantListResponse["results"][number]["id"][],
) =>
  useListPlantsQueryOptions(
    { id: ids },
    {
      query: {
        enabled: !!ids && ids.length > 0,
        select: (data) => data.results,
        staleTime: 1000 * 60 * 5, // 5 minutes
      },
    },
  );

export const purchaseGroupListQueryOptions = (ids: string[] | undefined) =>
  queryOptions({
    enabled: !!ids && ids.length > 0,
    queryFn: () => fetchPurchaseGroups({ filters: { id: ids } }),
    queryKey: ["purchaseGroups", "list", ids],
    select: (data) => data.results,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
